import React from 'react';
import { Box, Typography } from '@mui/material';
import { keyframes } from '@mui/system';
import { useTranslation } from '../../hooks/useTranslation';

// Animation pour le logo qui s'agrandit
const logoScale = keyframes`
  0% {
    transform: scale(0.8);
    opacity: 0.7;
  }
  50% {
    transform: scale(1.1);
    opacity: 1;
  }
  100% {
    transform: scale(0.8);
    opacity: 0.7;
  }
`;

// Animation de points qui défilent
const dotPulse = keyframes`
  0%, 20% {
    color: #F5F5DC;
    transform: scale(1);
  }
  40% {
    color: #000000;
    transform: scale(1.2);
  }
  60%, 100% {
    color: #6B7280;
    transform: scale(1);
  }
`;

// Animation de barres colorées
const colorWave = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

// Animation de rotation avec dégradé
const spinGradient = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const LoadingSpinner = ({
  type = 'logo',
  size = 'medium',
  message,
  showMessage = true
}) => {
  const { t } = useTranslation();
  const defaultMessage = message || t('common.loading');
  const getSizeValues = () => {
    switch (size) {
      case 'small':
        return { width: 40, height: 40, fontSize: '0.875rem' };
      case 'large':
        return { width: 80, height: 80, fontSize: '1.25rem' };
      default:
        return { width: 60, height: 60, fontSize: '1rem' };
    }
  };

  const { width, height, fontSize } = getSizeValues();

  const renderLogoSpinner = () => (
    <Box
      sx={{
        width,
        height,
        borderRadius: '50%',
        background: 'linear-gradient(45deg, #F5F5DC 30%, #000000 90%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: `${logoScale} 1.5s ease-in-out infinite`,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
      }}
    >
      <Typography
        variant="h6"
        sx={{
          color: '#FFFFFF',
          fontWeight: 700,
          fontSize: fontSize,
          textShadow: '1px 1px 2px rgba(0, 0, 0, 0.3)'
        }}
      >
        Z
      </Typography>
    </Box>
  );

  const renderDotSpinner = () => (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      {[0, 1, 2].map((index) => (
        <Box
          key={index}
          sx={{
            width: width / 4,
            height: width / 4,
            borderRadius: '50%',
            backgroundColor: '#F5F5DC',
            animation: `${dotPulse} 1.4s ease-in-out infinite`,
            animationDelay: `${index * 0.16}s`
          }}
        />
      ))}
    </Box>
  );

  const renderWaveSpinner = () => (
    <Box
      sx={{
        width,
        height: height / 4,
        borderRadius: height / 8,
        background: 'linear-gradient(90deg, #F5F5DC, #000000, #FFFFFF, #6B7280, #F5F5DC)',
        backgroundSize: '400% 400%',
        animation: `${colorWave} 2s ease infinite`
      }}
    />
  );

  const renderGradientSpinner = () => (
    <Box
      sx={{
        width,
        height,
        borderRadius: '50%',
        background: `conic-gradient(from 0deg, #F5F5DC, #000000, #F5F5DC)`,
        animation: `${spinGradient} 1s linear infinite`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&::before': {
          content: '""',
          width: width * 0.7,
          height: height * 0.7,
          borderRadius: '50%',
          backgroundColor: '#FFFFFF',
          position: 'absolute'
        }
      }}
    />
  );

  const renderSpinner = () => {
    switch (type) {
      case 'dots':
        return renderDotSpinner();
      case 'wave':
        return renderWaveSpinner();
      case 'gradient':
        return renderGradientSpinner();
      default:
        return renderLogoSpinner();
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        py: 4
      }}
    >
      {renderSpinner()}
      
      {showMessage && (
        <Typography
          variant="body2"
          sx={{
            color: '#6B7280',
            fontWeight: 500,
            fontSize,
            textAlign: 'center',
            letterSpacing: '0.5px'
          }}
        >
          {defaultMessage}
        </Typography>
      )}
    </Box>
  );
};

export default LoadingSpinner;